// Firebase functions for job-related operations
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase-config";

/**
 * Adds a job recommendation to a user's profile
 * @param {string} userId - The ID of the user to update
 * @param {Object} jobData - The job data to add
 * @returns {Promise<Object>} - Success status and data or error
 */
export const addJobRecommendation = async (userId, jobData) => {
  try {
    console.log('Adding job recommendation for user:', userId);
    
    if (!userId || !jobData) {
      return { success: false, error: 'Missing required parameters' };
    }
    
    const userProfileRef = doc(db, 'user_profiles', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    
    if (!userProfileSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }
    
    const userData = userProfileSnap.data();
    const jobRecommendations = userData.jobRecommendations || [];
    
    // Add timestamp to job data
    const jobWithTimestamp = {
      ...jobData,
      recommendedAt: new Date().toISOString(),
      status: 'recommended'
    };
    
    // Add to the beginning of the array (most recent first)
    const updatedRecommendations = [jobWithTimestamp, ...jobRecommendations];
    
    // Update user profile
    await updateDoc(userProfileRef, {
      jobRecommendations: updatedRecommendations,
      lastUpdated: serverTimestamp()
    });
    
    return { 
      success: true, 
      data: jobWithTimestamp 
    };
  } catch (error) {
    console.error('Error adding job recommendation:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to add job recommendation' 
    };
  }
};

/**
 * Updates a user's reaction to a job (like/dislike/save)
 * @param {string} userId - The ID of the user
 * @param {string} jobId - The ID of the job
 * @param {string} reaction - The reaction type ('like', 'dislike', 'save', etc.)
 * @param {Object} metadata - Additional metadata about the reaction
 * @returns {Promise<Object>} - Success status and data or error
 */
export const updateJobReaction = async (userId, jobId, reaction, metadata = {}) => {
  try {
    console.log('Updating job reaction:', { userId, jobId, reaction, metadata });
    
    if (!userId || !jobId || !reaction) {
      return { success: false, error: 'Missing required parameters' };
    }
    
    // Update the job document with user reaction
    const jobRef = doc(db, 'jobs', jobId);
    const jobSnap = await getDoc(jobRef);
    
    if (!jobSnap.exists()) {
      return { success: false, error: 'Job not found' };
    }
    
    const jobData = jobSnap.data();
    const userReactions = jobData.userReactions || {};
    
    // Update user's reaction for this job
    const updatedUserReaction = {
      ...userReactions[userId],
      reaction: reaction,
      timestamp: new Date().toISOString(),
      ...metadata
    };
    
    await updateDoc(jobRef, {
      [`userReactions.${userId}`]: updatedUserReaction,
      lastUpdated: serverTimestamp()
    });
    
    // Also update user profile with job interaction
    const userProfileRef = doc(db, 'user_profiles', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    
    if (userProfileSnap.exists()) {
      const userData = userProfileSnap.data();
      const jobInteractions = userData.jobInteractions || {};
      
      await updateDoc(userProfileRef, {
        [`jobInteractions.${jobId}`]: {
          reaction: reaction,
          timestamp: new Date().toISOString(),
          jobTitle: jobData.title,
          companyName: jobData.companyName,
          ...metadata
        },
        lastUpdated: serverTimestamp()
      });
    }
    
    return { 
      success: true, 
      data: { 
        jobId, 
        reaction, 
        timestamp: new Date().toISOString() 
      } 
    };
  } catch (error) {
    console.error('Error updating job reaction:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to update job reaction' 
    };
  }
};

/**
 * Get jobs with pagination and filtering options
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of results to return
 * @param {string} options.lastVisible - Last document for pagination
 * @param {string} options.industry - Filter by industry
 * @param {string} options.location - Filter by location
 * @param {string} options.searchTerm - Search term for job title or company
 * @returns {Promise<Object>} - Success status and jobs data or error
 */
export const getJobs = async (options = {}) => {
  try {
    const { 
      limit: limitCount = 20, 
      lastVisible = null,
      industry = null,
      location = null,
      searchTerm = null,
      orderByField = 'createdAt',
      orderDirection = 'desc'
    } = options;
    
    let jobsQuery = collection(db, 'jobs');
    
    // Build the query
    const queryConstraints = [];
    
    // Add filters if provided
    if (industry) {
      queryConstraints.push(where('industry', '==', industry));
    }
    
    if (location) {
      queryConstraints.push(where('location', '==', location));
    }
    
    // Add ordering
    queryConstraints.push(orderBy(orderByField, orderDirection));
    
    // Add pagination
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }
    
    // Add limit
    queryConstraints.push(limit(limitCount));
    
    // Apply constraints
    jobsQuery = query(jobsQuery, ...queryConstraints);
    
    // Execute query
    const jobsSnapshot = await getDocs(jobsQuery);
    
    if (jobsSnapshot.empty) {
      return { 
        success: true, 
        data: {
          jobs: [],
          lastVisible: null,
          hasMore: false
        }
      };
    }
    
    // Get last document for pagination
    const lastDoc = jobsSnapshot.docs[jobsSnapshot.docs.length - 1];
    
    // Transform data
    let jobs = jobsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter by search term if provided
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      jobs = jobs.filter(job => 
        job.title?.toLowerCase().includes(term) || 
        job.companyName?.toLowerCase().includes(term) ||
        job.description?.toLowerCase().includes(term)
      );
    }
    
    return { 
      success: true, 
      data: {
        jobs,
        lastVisible: lastDoc,
        hasMore: jobs.length === limitCount
      }
    };
  } catch (error) {
    console.error('Error in getJobs:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to fetch jobs' 
    };
  }
};

/**
 * Get a specific job by ID
 * @param {string} jobId - The ID of the job to fetch
 * @returns {Promise<Object>} - Success status and job data or error
 */
export const getJobById = async (jobId) => {
  try {
    console.log('Fetching job:', jobId);
    
    const jobRef = doc(db, 'jobs', jobId);
    const jobSnap = await getDoc(jobRef);
    
    if (!jobSnap.exists()) {
      return { success: false, error: 'Job not found' };
    }
    
    return {
      success: true,
      data: {
        id: jobSnap.id,
        ...jobSnap.data()
      }
    };
  } catch (error) {
    console.error('Error fetching job:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get user's job interactions (likes, saves, applications, etc.)
 * @param {string} userId - The ID of the user
 * @returns {Promise<Object>} - Success status and interactions data or error
 */
export const getUserJobInteractions = async (userId) => {
  try {
    console.log('Fetching job interactions for user:', userId);
    
    const userProfileRef = doc(db, 'user_profiles', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    
    if (!userProfileSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }
    
    const userData = userProfileSnap.data();
    const jobInteractions = userData.jobInteractions || {};
    
    return {
      success: true,
      data: jobInteractions
    };
  } catch (error) {
    console.error('Error fetching user job interactions:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Remove a job recommendation from a user's profile
 * @param {string} userId - The ID of the user
 * @param {string} jobId - The ID of the job to remove
 * @returns {Promise<Object>} - Success status and data or error
 */
export const removeRecommendedJobFromUser = async (userId, jobId) => {
  try {
    console.log('Removing job recommendation for user:', userId, 'jobId:', jobId);
    
    const userProfileRef = doc(db, 'user_profiles', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    
    if (!userProfileSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }
    
    const userData = userProfileSnap.data();
    const jobRecommendations = userData.jobRecommendations || [];
    
    // Filter out the job to remove
    const updatedRecommendations = jobRecommendations.filter(job => job.id !== jobId);
    
    // Update user profile
    await updateDoc(userProfileRef, {
      jobRecommendations: updatedRecommendations,
      lastUpdated: serverTimestamp()
    });
    
    return { 
      success: true, 
      data: { jobId, removedAt: new Date().toISOString() }
    };
  } catch (error) {
    console.error('Error removing job recommendation:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to remove job recommendation' 
    };
  }
};

/**
 * Archive a job for a user
 * @param {string} userId - The ID of the user
 * @param {string} jobId - The ID of the job to archive
 * @returns {Promise<Object>} - Success status and data or error
 */
export const archiveJobForUser = async (userId, jobId) => {
  try {
    console.log('Archiving job for user:', userId, 'jobId:', jobId);
    
    const userProfileRef = doc(db, 'user_profiles', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    
    if (!userProfileSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }
    
    const userData = userProfileSnap.data();
    const archivedJobs = userData.archivedJobs || [];
    
    // Add to archived jobs if not already there
    if (!archivedJobs.includes(jobId)) {
      const updatedArchivedJobs = [...archivedJobs, jobId];
      
      // Update user profile
      await updateDoc(userProfileRef, {
        archivedJobs: updatedArchivedJobs,
        lastUpdated: serverTimestamp()
      });
    }
    
    return { 
      success: true, 
      data: { jobId, archivedAt: new Date().toISOString() }
    };
  } catch (error) {
    console.error('Error archiving job:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to archive job' 
    };
  }
};

/**
 * Delete a job from the database (admin only)
 * @param {string} jobId - The ID of the job to delete
 * @returns {Promise<Object>} - Success status and data or error
 */
export const deleteJob = async (jobId) => {
  try {
    console.log('Deleting job:', jobId);
    
    // Note: This is a placeholder implementation
    // In a real implementation, you would delete the job document
    // and clean up any references to it in user profiles
    
    const jobRef = doc(db, 'jobs', jobId);
    const jobSnap = await getDoc(jobRef);
    
    if (!jobSnap.exists()) {
      return { success: false, error: 'Job not found' };
    }
    
    // For now, just mark as deleted instead of actually deleting
    await updateDoc(jobRef, {
      deleted: true,
      deletedAt: serverTimestamp()
    });
    
    return { 
      success: true, 
      data: { jobId, deletedAt: new Date().toISOString() }
    };
  } catch (error) {
    console.error('Error deleting job:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to delete job' 
    };
  }
};